/*
 * /fishing-knots — the 3D knot viewer.
 *
 * Data: public/data/fishing-knots/knots.json (scripts/build-fishing-knots.mjs). Every strand of a
 * knot comes as control points in two states, LOOSE (how it is tied) and TIGHT (dressed); the page
 * draws a centripetal Catmull-Rom tube through them — the same curve the geometry test measures —
 * reveals it along the tying timeline and morphs loose → tight in the "pull" step.
 *
 * Rendering is on demand: nothing runs while the animation is paused and nobody touches the view
 * (a 3D page that spins its GPU in idle drains a phone for nothing).
 */
(function () {
    'use strict';

    var CFG = window.__FKNOTS || {};
    var I = CFG.i18n || {};
    var SLUG = 'fishing-knots';
    var root = document.getElementById('fk');
    if (!root) return;

    function $(id) { return document.getElementById(id); }
    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
    function smooth(x) { x = clamp(x, 0, 1); return x * x * (3 - 2 * x); }
    function txt(path, fallback) {
        var o = I, parts = path.split('.');
        for (var i = 0; i < parts.length; i++) { if (o == null) return fallback || ''; o = o[parts[i]]; }
        return o == null ? (fallback || '') : o;
    }
    function fmt(s, vars) {
        return String(s || '').replace(/:([a-z_]+)/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; });
    }
    function stage(name, meta) { try { if (typeof window.__stage === 'function') window.__stage(name, meta || {}); } catch (_) {} }
    function settingLoad(k, d) { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (_) { return d; } }
    function settingSave(k, v) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (_) {} }

    // ── device class ────────────────────────────────────────────────────────
    var coarse = false;
    try { coarse = window.matchMedia('(pointer: coarse)').matches; } catch (_) {}
    // Объём памяти — только из общего бюджета (window.__memBudget): в WebKit
    // navigator.deviceMemory нет, и своя «страховка» решала бы за iPhone втихую.
    var memGb = 8;
    try { if (window.__memBudget && window.__memBudget.gb) memGb = window.__memBudget.gb(); } catch (_) {}
    var lowEnd = coarse || (navigator.hardwareConcurrency || 8) <= 4 || memGb <= 4;
    var reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) {}
    var Q = lowEnd
        ? { seg: 34, radial: 8, shadows: false, dpr: 1.5, aa: false }
        : { seg: 70, radial: 14, shadows: true, dpr: 2, aa: true };

    // ── DOM ─────────────────────────────────────────────────────────────────
    var view = $('fk-view'), canvas = $('fk-canvas'), statusEl = $('fk-status');
    var playBtn = $('fk-play'), prevBtn = $('fk-prev'), nextBtn = $('fk-next');
    var scrub = $('fk-scrub'), speedSel = $('fk-speed');
    var capNum = $('fk-cap-n'), capText = $('fk-cap-t');
    var stepsList = $('fk-steps');

    var data = null, knotById = {}, current = null;
    var state = { t: 0, playing: false, speed: 1, lastTs: 0, completed: {} };

    // ── three.js scene ──────────────────────────────────────────────────────
    var THREE = window.THREE;
    var renderer = null, scene, camera, ground, keyLight, knotGroup;
    var materials = {};
    var model = null;          // { strands: [...], props: [...] }
    var needsRender = false, rafId = 0;

    function fail(reasonKey) {
        root.classList.add('fk-nogl');
        if (statusEl) { statusEl.textContent = txt('ui.' + reasonKey); statusEl.hidden = false; }
    }

    function initGL() {
        if (!THREE) { fail('no_webgl'); return false; }
        try {
            renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: Q.aa, alpha: true, powerPreference: 'default' });
        } catch (e) {
            fail('no_webgl');
            try { if (typeof window.__aeDiagnostic === 'function') window.__aeDiagnostic({ issue: 'webgl_unavailable' }); } catch (_) {}
            return false;
        }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, Q.dpr));
        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.shadowMap.enabled = Q.shadows;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(32, 1, 0.05, 60);

        var hemi = new THREE.HemisphereLight(0xffffff, 0x3a4252, 0.9);
        scene.add(hemi);
        keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
        keyLight.position.set(1.6, 4.2, 3.2);
        keyLight.castShadow = Q.shadows;
        if (Q.shadows) {
            keyLight.shadow.mapSize.set(1024, 1024);
            var sc = keyLight.shadow.camera;
            sc.left = -3; sc.right = 3; sc.top = 3; sc.bottom = -3; sc.near = 0.5; sc.far = 12;
            keyLight.shadow.radius = 4;
            keyLight.shadow.bias = -0.0008;
        }
        scene.add(keyLight);
        scene.add(keyLight.target);
        var fill = new THREE.DirectionalLight(0xb9ccff, 0.35);
        fill.position.set(-3, 1.5, 2);
        scene.add(fill);

        if (Q.shadows) {
            ground = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), new THREE.ShadowMaterial({ opacity: 0.2 }));
            ground.rotation.x = -Math.PI / 2;
            ground.receiveShadow = true;
            scene.add(ground);
        }

        knotGroup = new THREE.Group();
        scene.add(knotGroup);

        materials.mono = new THREE.MeshStandardMaterial({ color: 0xf2d14a, roughness: 0.38, metalness: 0.0 });
        materials.mono2 = new THREE.MeshStandardMaterial({ color: 0xff7f3f, roughness: 0.4, metalness: 0.0 });
        materials.fluoro = new THREE.MeshStandardMaterial({ color: 0xf3a7c6, roughness: 0.3, metalness: 0.0 });
        materials.braid = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.75, metalness: 0.0, map: braidTexture() });
        materials.metal = new THREE.MeshStandardMaterial({ color: 0xc4cad3, roughness: 0.3, metalness: 0.65 });
        materials.metalDark = new THREE.MeshStandardMaterial({ color: 0x8b939e, roughness: 0.35, metalness: 0.7 });
        materials.spool = new THREE.MeshStandardMaterial({ color: 0x2b3442, roughness: 0.55, metalness: 0.2 });

        canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); stop(); fail('gl_lost'); }, false);
        canvas.addEventListener('webglcontextrestored', function () { root.classList.remove('fk-nogl'); if (statusEl) statusEl.hidden = true; if (current) loadKnot(current.id, true); }, false);
        return true;
    }

    /** Woven look for braid: diagonal stripes in two greens, repeated along the tube. */
    function braidTexture() {
        var c = document.createElement('canvas');
        c.width = 64; c.height = 32;
        var g = c.getContext('2d');
        g.fillStyle = '#2f8f55'; g.fillRect(0, 0, 64, 32);
        g.strokeStyle = '#4fbf7c'; g.lineWidth = 7;
        for (var x = -64; x < 128; x += 16) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x + 32, 32); g.stroke(); }
        g.strokeStyle = 'rgba(15,60,35,.55)'; g.lineWidth = 2;
        for (x = -64; x < 128; x += 16) { g.beginPath(); g.moveTo(x + 8, 0); g.lineTo(x + 40, 32); g.stroke(); }
        var tex = new THREE.CanvasTexture(c);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.encoding = THREE.sRGBEncoding;
        return tex;
    }

    // ── geometry ────────────────────────────────────────────────────────────
    function vecs(arr) { var out = new Array(arr.length); for (var i = 0; i < arr.length; i++) out[i] = new THREE.Vector3(arr[i][0], arr[i][1], arr[i][2]); return out; }

    function curveOf(pts) { return new THREE.CatmullRomCurve3(pts, false, 'centripetal'); }

    /** Arc fraction of a curve parameter t (0..1 over control points). */
    function arcFracOfT(curve, t) {
        var L = curve.getLengths(), n = L.length - 1;
        var f = clamp(t, 0, 1) * n, i = Math.floor(f), w = f - i;
        if (i >= n) return 1;
        return (L[i] + (L[i + 1] - L[i]) * w) / L[n];
    }

    function buildStrandMesh(s, pts) {
        var curve = curveOf(pts);
        var len = curve.getLength();
        var tubular = clamp(Math.round(len * Q.seg), 24, 2600);
        var geo = new THREE.TubeGeometry(curve, tubular, s.r, Q.radial, false);
        if (s.mesh) { s.mesh.geometry.dispose(); s.mesh.geometry = geo; }
        else {
            s.mesh = new THREE.Mesh(geo, materials[s.kind] || materials.mono);
            s.mesh.castShadow = Q.shadows;
            s.mesh.receiveShadow = false;
            knotGroup.add(s.mesh);
            s.capA = new THREE.Mesh(new THREE.SphereGeometry(s.r * 1.02, Q.radial, Math.max(4, Q.radial >> 1)), s.mesh.material);
            s.capB = s.capA.clone();
            s.capA.castShadow = s.capB.castShadow = Q.shadows;
            knotGroup.add(s.capA); knotGroup.add(s.capB);
        }
        if (s.kind === 'braid' && s.mesh.material.map) {
            s.mesh.material.map.repeat.set(Math.max(1, len / 0.09), 1);
        }
        s.curve = curve; s.tubular = tubular; s.len = len;
    }

    function disposeModel() {
        if (!model) return;
        for (var i = knotGroup.children.length - 1; i >= 0; i--) {
            var o = knotGroup.children[i];
            knotGroup.remove(o);
            if (o.geometry) o.geometry.dispose();
        }
        model = null;
    }

    function makeProps(k) {
        var props = k.props || [], R = data.ring;
        for (var i = 0; i < props.length; i++) {
            var p = props[i], m;
            if (p.type === 'ring') {
                // a barrel swivel: the eye the line goes through, the body hanging below it and the
                // lower eye turned a quarter — the same obstacle the build dressed the line around
                m = new THREE.Mesh(new THREE.TorusGeometry(R.R, R.rr, 14, 56), materials.metal);
                m.rotation.y = Math.PI / 2;
                addProp(m);
                var body = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, 0.44, 20), materials.metalDark);
                body.position.set(0, -R.R - R.rr - 0.22, 0);
                addProp(body);
                var ring2 = new THREE.Mesh(new THREE.TorusGeometry(0.15, R.rr * 0.9, 12, 40), materials.metal);
                ring2.position.set(0, -R.R - R.rr - 0.44 - 0.15, 0);
                addProp(ring2);
            } else if (p.type === 'hook') {
                addHook(p);
            } else if (p.type === 'spool') {
                var sp = new THREE.Mesh(new THREE.CylinderGeometry(p.r || 0.42, p.r || 0.42, p.w || 1.4, 40, 1, true), materials.spool);
                sp.rotation.z = Math.PI / 2;
                sp.position.set(p.x || 0, p.y || 0, p.z || 0);
                addProp(sp);
                var flange = new THREE.CylinderGeometry((p.r || 0.42) * 1.9, (p.r || 0.42) * 1.9, 0.05, 48);
                for (var sgn = -1; sgn <= 1; sgn += 2) {
                    var f = new THREE.Mesh(flange, materials.metalDark);
                    f.rotation.z = Math.PI / 2;
                    f.position.set((p.x || 0) + sgn * (p.w || 1.4) / 2, p.y || 0, p.z || 0);
                    addProp(f);
                }
            }
        }
    }
    function addProp(m) { m.castShadow = Q.shadows; m.receiveShadow = Q.shadows; knotGroup.add(m); }

    /** A hook along +x from its spade/eye at x = 0: shank, bend and point. */
    function addHook(p) {
        var L = p.shank || 2.2, w = p.wire || 0.05, gap = p.gap || 0.62;
        var pts = [new THREE.Vector3(0, p.y || 0, 0), new THREE.Vector3(L * 0.5, p.y || 0, 0), new THREE.Vector3(L, p.y || 0, 0)];
        var bendC = new THREE.Vector3(L, (p.y || 0) - gap / 2, 0);
        for (var a = 1; a <= 10; a++) {
            var ang = Math.PI / 2 - Math.PI * a / 10;
            pts.push(new THREE.Vector3(bendC.x + Math.cos(ang) * gap / 2, bendC.y + Math.sin(ang) * gap / 2, 0));
        }
        pts.push(new THREE.Vector3(L - 0.45, (p.y || 0) - gap + 0.02, 0));
        pts.push(new THREE.Vector3(L - 0.75, (p.y || 0) - gap + 0.16, 0));
        var c = new THREE.CatmullRomCurve3(pts, false, 'centripetal');
        addProp(new THREE.Mesh(new THREE.TubeGeometry(c, 90, w, 12, false), materials.metal));
        var tip = new THREE.Mesh(new THREE.ConeGeometry(w * 1.05, 0.16, 12), materials.metal);
        var end = pts[pts.length - 1], prev = pts[pts.length - 2];
        tip.position.copy(end);
        tip.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), end.clone().sub(prev).normalize());
        addProp(tip);
        if (p.spade) {
            var sp = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.2, 0.2), materials.metal);
            sp.position.set(-0.03, p.y || 0, 0);
            addProp(sp);
        }
    }

    // ── a knot ──────────────────────────────────────────────────────────────
    function markIndex(s, m) {
        if (typeof m === 'number') return m;
        if (Array.isArray(m)) return (s.marks[m[0]] || 0) + (m[1] || 0);
        return s.marks[m] != null ? s.marks[m] : 0;
    }

    // Geometry comes per knot (a few tens of KB each) — the page loads one, not eighteen.
    var geoCache = {}, geoWait = {};
    function fetchGeo(id) {
        if (geoCache[id]) return Promise.resolve(geoCache[id]);
        if (geoWait[id]) return geoWait[id];
        geoWait[id] = fetch(CFG.geo.replace('__ID__', id), { credentials: 'same-origin' })
            .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
            .then(function (g) { geoCache[id] = g; delete geoWait[id]; return g; })
            .catch(function (e) { delete geoWait[id]; throw e; });
        return geoWait[id];
    }

    var loadSeq = 0;
    function loadKnot(id, keepTime, then) {
        var k = knotById[id];
        if (!k) return;
        current = k;
        if (!keepTime) { state.t = 0; }
        renderInfo(k);
        markUrl(k.id);
        stage('knot', { knot: k.id });
        var seq = ++loadSeq;
        if (!renderer) { updateTimelineUI(); if (then) then(); return; }
        disposeModel();
        // the previous knot must not stay on screen under the new knot's captions while this one loads
        requestRender();
        if (!geoCache[id] && statusEl) { statusEl.textContent = txt('ui.loading'); statusEl.hidden = false; }
        fetchGeo(id).then(function (g) {
            if (seq !== loadSeq) return;   // the person already chose another knot
            if (statusEl) statusEl.hidden = true;
            buildModel(k, g);
            apply(state.t, true);
            if (then) then();
        }).catch(function (err) {
            if (seq !== loadSeq) return;
            fail('load_failed');
            try { if (typeof window.__handleError === 'function') window.__handleError(err, 'fishing_knots_geometry', { say: txt('ui.load_failed') }); } catch (_) {}
        });
    }

    function buildModel(k, g) {
        disposeModel();
        model = { strands: [] };
        makeProps(k);
        for (var i = 0; i < g.strands.length; i++) {
            var src = g.strands[i];
            var frames = src.frames.map(function (f) {
                var out = new Array(f.length / 3);
                for (var j = 0; j < out.length; j++) out[j] = new THREE.Vector3(f[3 * j], f[3 * j + 1], f[3 * j + 2]);
                return out;
            });
            var n = frames[0].length;
            var s = {
                id: src.id, kind: src.kind, r: data.rad[src.kind] || 0.05, marks: src.marks,
                frames: frames, n: n,
                reveal: (k.reveal && k.reveal[src.id]) || [[0, n - 1]],
                trim: k.trim && k.trim[src.id], m: -1, cur: frames[0].map(function (v) { return v.clone(); }),
            };
            model.strands.push(s);
        }
        frameView(k);
    }

    /** The visible part of a strand at timeline t: [from, to] as control-point indices. */
    function revealRange(s, t) {
        var R = s.reveal, idx;
        if (t <= R[0][0]) idx = markIndex(s, R[0][1]);
        else if (t >= R[R.length - 1][0]) idx = markIndex(s, R[R.length - 1][1]);
        else {
            for (var i = 1; i < R.length; i++) {
                if (t <= R[i][0]) {
                    var a = R[i - 1], b = R[i], w = (t - a[0]) / Math.max(1e-6, b[0] - a[0]);
                    idx = markIndex(s, a[1]) + (markIndex(s, b[1]) - markIndex(s, a[1])) * w;
                    break;
                }
            }
        }
        var from = 0;
        if (s.trim && t > s.trim[0]) {
            var w2 = smooth((t - s.trim[0]) / Math.max(1e-6, s.trim[1] - s.trim[0]));
            var keep = markIndex(s, s.trim[2]);
            // 'start' trims a tag that sits at the strand's beginning (the line is drawn from the tag)
            if (s.trim[3] === 'start') from = keep * w2;
            else idx = idx + (keep - idx) * w2;
        }
        return [clamp(from, 0, s.n - 1), clamp(idx, 0, s.n - 1)];
    }

    function tightAt(k, t) {
        if (!k.tighten) return 0;
        return smooth((t - k.tighten[0]) / Math.max(1e-6, k.tighten[1] - k.tighten[0]));
    }

    /** Points of a strand at tightening m: piecewise between the stored dressed frames. */
    function morphTo(s, m) {
        var F = s.frames, last = F.length - 1;
        var x = clamp(m, 0, 1) * last, i0 = Math.min(last - 1, Math.floor(x)), w = x - i0;
        if (last === 0) { i0 = 0; w = 0; }
        var A = F[i0], B = F[Math.min(last, i0 + 1)];
        for (var j = 0; j < s.n; j++) s.cur[j].lerpVectors(A[j], B[j], w);
    }

    function apply(t, force) {
        var k = current;
        if (!k) return;
        state.t = clamp(t, 0, 1);
        if (model && renderer) {
            var m = tightAt(k, state.t);
            for (var i = 0; i < model.strands.length; i++) {
                var s = model.strands[i];
                if (force || Math.abs(m - s.m) > 1e-4) {
                    morphTo(s, m);
                    buildStrandMesh(s, s.cur);
                    s.m = m;
                }
                var r = revealRange(s, state.t);
                var f0 = r[0] > 0 ? arcFracOfT(s.curve, r[0] / (s.n - 1)) : 0;
                var f1 = arcFracOfT(s.curve, r[1] / (s.n - 1));
                var s0 = Math.round(f0 * s.tubular), s1 = Math.max(s0 + 1, Math.round(f1 * s.tubular));
                s.mesh.geometry.setDrawRange(s0 * Q.radial * 6, (s1 - s0) * Q.radial * 6);
                s.capA.position.copy(s.curve.getPointAt(clamp(s0 / s.tubular, 0, 1)));
                s.capB.position.copy(s.curve.getPointAt(clamp(s1 / s.tubular, 0, 1)));
                s.mesh.visible = f1 - f0 > 0.0005;
                s.capA.visible = s.capB.visible = s.mesh.visible;
            }
            requestRender();
        }
        updateTimelineUI();
    }

    // ── timeline UI ─────────────────────────────────────────────────────────
    function stepAt(t) {
        var st = current.steps;
        for (var i = 0; i < st.length; i++) if (t < st[i][2] - 1e-6) return i;
        return st.length - 1;
    }

    var lastStep = -1;
    function updateTimelineUI() {
        if (!current) return;
        if (scrub && document.activeElement !== scrub) scrub.value = String(Math.round(state.t * 1000));
        var i = stepAt(state.t), st = current.steps;
        if (i !== lastStep) {
            lastStep = i;
            var steps = txt('knots.' + current.id + '.steps', []);
            if (capNum) capNum.textContent = fmt(txt('ui.step_n'), { n: i + 1, total: st.length });
            if (capText) capText.textContent = steps[st[i][0]] || '';
            if (stepsList) {
                var items = stepsList.querySelectorAll('[data-step]');
                for (var j = 0; j < items.length; j++) items[j].classList.toggle('on', +items[j].getAttribute('data-step') === i);
            }
        }
        if (playBtn) {
            playBtn.setAttribute('aria-pressed', state.playing ? 'true' : 'false');
            playBtn.setAttribute('aria-label', state.playing ? txt('ui.pause') : txt('ui.play'));
            playBtn.classList.toggle('is-playing', state.playing);
        }
    }

    function play() {
        if (!current) return;
        if (state.t >= 0.999) apply(0, false);
        state.playing = true;
        state.lastTs = 0;
        stage('play', { knot: current.id });
        loop();
        updateTimelineUI();
    }
    function stop() { state.playing = false; updateTimelineUI(); }
    function toggle() { if (state.playing) stop(); else play(); }

    function seekStep(delta) {
        if (!current) return;
        stop();
        var i = clamp(stepAt(state.t) + delta, 0, current.steps.length - 1);
        // "next" from the last step shows the finished knot
        if (delta > 0 && stepAt(state.t) === current.steps.length - 1) { apply(1, false); return; }
        apply(current.steps[i][1] + 1e-4, false);
    }

    function loop() {
        if (rafId) return;
        rafId = requestAnimationFrame(frame);
    }
    function frame(ts) {
        rafId = 0;
        if (state.playing && current) {
            if (!state.lastTs) state.lastTs = ts;
            var dt = Math.min(0.1, (ts - state.lastTs) / 1000);
            state.lastTs = ts;
            var nt = state.t + dt * state.speed / current.dur;
            if (nt >= 1) {
                nt = 1; state.playing = false;
                if (!state.completed[current.id]) {
                    state.completed[current.id] = 1;
                    try { if (typeof window.__aeToolComplete === 'function') window.__aeToolComplete({ knot: current.id }); } catch (_) {}
                }
            }
            apply(nt, false);
        }
        if (orbit.inertia()) needsRender = true;
        if (needsRender && renderer) { needsRender = false; renderer.render(scene, camera); }
        if (state.playing || orbit.moving()) loop();
    }
    function requestRender() { needsRender = true; loop(); }

    // ── camera: orbit that lets a phone scroll the page ──────────────────────
    // One finger dragging sideways turns the knot; dragging up/down scrolls the page (touch-action:
    // pan-y on the canvas). Two fingers pinch to zoom and tilt. The wheel zooms only while the
    // canvas has focus — a wheel that eats page scrolling on a long page is a trap.
    var orbit = (function () {
        var az = 0.35, el = 0.32, dist = 4;
        var tgt = null, vAz = 0, minD = 0.8, maxD = 14;
        var ptrs = {}, nPtr = 0, drag = null, pinch = null;

        function setTarget(v, d) { tgt = v; dist = d; minD = d * 0.25; maxD = d * 3; }
        function place() {
            if (!camera || !tgt) return;
            var ce = Math.cos(el);
            camera.position.set(tgt.x + dist * ce * Math.sin(az), tgt.y + dist * Math.sin(el), tgt.z + dist * ce * Math.cos(az));
            camera.lookAt(tgt);
            if (keyLight) { keyLight.target.position.copy(tgt); }
            requestRender();
        }
        function reset(k) {
            az = (k.view && k.view.az != null) ? k.view.az : 0.42;
            el = (k.view && k.view.el != null) ? k.view.el : 0.36;
            vAz = 0;
            var c = k.view && k.view.c ? k.view.c : [0.6, 0.1, 0];
            var r = k.view && k.view.r ? k.view.r : 1.3;
            var aspect = camera ? Math.max(0.5, camera.aspect) : 1.4;
            var fov = (camera ? camera.fov : 32) * Math.PI / 180;
            var d = r / Math.sin(fov / 2) * (aspect < 1 ? 0.9 / aspect : 0.86);
            setTarget(new THREE.Vector3(c[0], c[1], c[2]), d);
            if (ground) ground.position.y = c[1] - r * 0.95;
            place();
        }
        function rotate(dAz, dEl) { az += dAz; el = clamp(el + dEl, -1.35, 1.35); place(); }
        function zoom(f) { dist = clamp(dist * f, minD, maxD); place(); }

        function onDown(e) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
            nPtr = Object.keys(ptrs).length;
            if (nPtr === 1) { drag = { x: e.clientX, y: e.clientY, live: false, touch: e.pointerType !== 'mouse' }; vAz = 0; }
            else if (nPtr === 2) { drag = null; pinch = pinchState(); }
        }
        function pinchState() {
            var k = Object.keys(ptrs); if (k.length < 2) return null;
            var a = ptrs[k[0]], b = ptrs[k[1]];
            return { d: Math.hypot(a.x - b.x, a.y - b.y), y: (a.y + b.y) / 2 };
        }
        function onMove(e) {
            if (!ptrs[e.pointerId]) return;
            ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
            if (pinch && nPtr >= 2) {
                var p = pinchState();
                if (p && pinch.d > 0) {
                    zoom(clamp(pinch.d / Math.max(1, p.d), 0.5, 2));
                    rotate(0, (p.y - pinch.y) * 0.006);
                    pinch = p;
                }
                e.preventDefault();
                return;
            }
            if (!drag) return;
            var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
            if (!drag.live) {
                // a finger trembles: a tap must stay a tap (threshold), and on touch only a
                // mostly sideways move is ours — a vertical one is the page scrolling
                if (Math.hypot(dx, dy) < (drag.touch ? 8 : 3)) return;
                if (drag.touch && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
                drag.live = true;
                try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
                stopIntro();
            }
            var w = Math.max(240, canvas.clientWidth);
            var dAz = -dx / w * 3.2, dEl = drag.touch ? 0 : dy / w * 2.4;
            vAz = dAz;
            rotate(dAz, dEl);
            drag.x = e.clientX; drag.y = e.clientY;
            e.preventDefault();
        }
        function onUp(e) {
            delete ptrs[e.pointerId];
            nPtr = Object.keys(ptrs).length;
            if (nPtr < 2) pinch = null;
            if (nPtr === 0) { drag = null; loop(); }
        }
        function onWheel(e) {
            if (document.activeElement !== canvas && !(e.ctrlKey)) return;
            e.preventDefault();
            zoom(e.deltaY > 0 ? 1.1 : 1 / 1.1);
        }
        function onKey(e) {
            var step = 0.12;
            if (e.key === 'ArrowLeft') rotate(step, 0);
            else if (e.key === 'ArrowRight') rotate(-step, 0);
            else if (e.key === 'ArrowUp') rotate(0, step);
            else if (e.key === 'ArrowDown') rotate(0, -step);
            else if (e.key === '+' || e.key === '=') zoom(1 / 1.15);
            else if (e.key === '-' || e.key === '_') zoom(1.15);
            else return;
            e.preventDefault();
            e.stopPropagation();
        }
        var introTimer = 0;
        function stopIntro() { if (introTimer) { clearInterval(introTimer); introTimer = 0; } }
        return {
            attach: function () {
                canvas.addEventListener('pointerdown', onDown);
                canvas.addEventListener('pointermove', onMove, { passive: false });
                canvas.addEventListener('pointerup', onUp);
                canvas.addEventListener('pointercancel', onUp);
                canvas.addEventListener('wheel', onWheel, { passive: false });
                canvas.addEventListener('keydown', onKey);
                canvas.addEventListener('dblclick', function () { if (current) reset(current); });
            },
            reset: reset, rotate: rotate, zoom: zoom, place: place, stopIntro: stopIntro,
            moving: function () { return !!drag || Math.abs(vAz) > 1e-4; },
            inertia: function () {
                if (drag || Math.abs(vAz) < 1e-4) { if (!drag) vAz = 0; return false; }
                vAz *= 0.9; az += vAz; place(); return true;
            },
        };
    })();

    function frameView(k) { orbit.reset(k); }

    function resize() {
        if (!renderer) return;
        var w = view.clientWidth, h = view.clientHeight;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        if (current) orbit.reset(current); else requestRender();
    }

    // ── info panel ──────────────────────────────────────────────────────────
    function renderInfo(k) {
        var K = txt('knots.' + k.id, {});
        var name = $('fk-name'), aka = $('fk-aka'), use = $('fk-use'), tips = $('fk-tips'), str = $('fk-strength');
        if (name) name.textContent = K.name || k.id;
        if (aka) { aka.textContent = K.aka || ''; aka.hidden = !K.aka; }
        if (use) use.textContent = K.use || '';
        if (stepsList) {
            stepsList.innerHTML = '';
            var steps = K.steps || [];
            for (var i = 0; i < k.steps.length; i++) {
                var li = document.createElement('li');
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'fk-step';
                b.setAttribute('data-step', String(i));
                b.textContent = steps[k.steps[i][0]] || '';
                li.appendChild(b);
                stepsList.appendChild(li);
            }
        }
        if (tips) {
            tips.innerHTML = '';
            (K.tips || []).forEach(function (s) { var li = document.createElement('li'); li.textContent = s; tips.appendChild(li); });
            $('fk-tips-box').hidden = !(K.tips && K.tips.length);
        }
        if (str) {
            var rows = k.strength || [];
            str.innerHTML = '';
            var srcBox = $('fk-strength-src');
            if (rows.length) {
                rows.forEach(function (r) {
                    var li = document.createElement('li');
                    var w = document.createElement('span'); w.textContent = txt('ui.strength_' + r.what, r.what) + (r.turns ? ' · ' + fmt(txt('ui.turns_n'), { n: r.turns }) : '');
                    var v = document.createElement('b'); v.textContent = r.pct == null ? txt('ui.strength_slipped') : fmt(txt('ui.pct'), { n: r.pct });
                    li.appendChild(w); li.appendChild(v);
                    str.appendChild(li);
                });
                if (srcBox) srcBox.hidden = false;
            } else {
                var li = document.createElement('li');
                li.className = 'fk-none';
                li.textContent = txt('ui.strength_none');
                str.appendChild(li);
                if (srcBox) srcBox.hidden = true;
            }
        }
        var btns = root.querySelectorAll('[data-knot]');
        for (var j = 0; j < btns.length; j++) {
            var on = btns[j].getAttribute('data-knot') === k.id;
            btns[j].classList.toggle('on', on);
            btns[j].setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        lastStep = -1;
        if (scrub) scrub.setAttribute('aria-valuetext', K.name || k.id);
    }

    function markUrl(id) {
        try {
            var u = new URL(location.href);
            if (u.searchParams.get('knot') === id) return;
            u.searchParams.set('knot', id);
            history.replaceState(history.state, '', u.toString());
        } catch (_) {}
    }

    // ── "which knot for what" ─────────────────────────────────────────────────
    function renderPick() {
        var out = $('fk-pick-out');
        if (!out) return;
        var lineEl = root.querySelector('[data-pick-line].on'), toEl = root.querySelector('[data-pick-to].on');
        var line = lineEl ? lineEl.getAttribute('data-pick-line') : 'mono';
        var to = toEl ? toEl.getAttribute('data-pick-to') : 'eye';
        var rules = (data.pick || {})[to] || [];
        out.innerHTML = '';
        var any = false;
        rules.forEach(function (r) {
            if (r.lines && r.lines.indexOf(line) < 0) return;
            if (!knotById[r.knot]) return;
            any = true;
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'fk-pick-item';
            b.setAttribute('data-knot', r.knot);
            var nm = document.createElement('b'); nm.textContent = txt('knots.' + r.knot + '.name', r.knot);
            var why = document.createElement('span'); why.textContent = txt('reasons.' + r.why, '');
            b.appendChild(nm); b.appendChild(why);
            if (current && current.id === r.knot) b.classList.add('on');
            out.appendChild(b);
        });
        var note = (data.pickNotes || {})[to + ':' + line];
        if (note) { var p = document.createElement('p'); p.className = 'tc-hint fk-pick-note'; p.textContent = txt('reasons.' + note, ''); out.appendChild(p); }
        if (!any) { var e = document.createElement('p'); e.className = 'tc-hint'; e.textContent = txt('ui.pick_none'); out.appendChild(e); }
    }

    // ── wiring ──────────────────────────────────────────────────────────────
    function onClick(e) {
        var t = e.target;
        var kb = t.closest && t.closest('[data-knot]');
        if (kb && root.contains(kb)) {
            stop();
            loadKnot(kb.getAttribute('data-knot'), false);
            renderPick();
            if (!reduceMotion) play();
            return;
        }
        var lb = t.closest && t.closest('[data-pick-line],[data-pick-to]');
        if (lb && root.contains(lb)) {
            var attr = lb.hasAttribute('data-pick-line') ? 'data-pick-line' : 'data-pick-to';
            var sibs = root.querySelectorAll('[' + attr + ']');
            for (var i = 0; i < sibs.length; i++) { var on = sibs[i] === lb; sibs[i].classList.toggle('on', on); sibs[i].setAttribute('aria-pressed', on ? 'true' : 'false'); }
            settingSave(attr === 'data-pick-line' ? 'line' : 'to', lb.getAttribute(attr));
            renderPick();
            stage('pick', { line: (root.querySelector('[data-pick-line].on') || {}).getAttribute && root.querySelector('[data-pick-line].on').getAttribute('data-pick-line') });
            return;
        }
        var sb = t.closest && t.closest('.fk-step');
        if (sb && current) {
            stop();
            var i2 = +sb.getAttribute('data-step');
            apply(current.steps[i2][1] + 1e-4, false);
            if (!reduceMotion) play();
        }
    }

    function wire() {
        root.addEventListener('click', onClick);
        if (playBtn) playBtn.addEventListener('click', toggle);
        if (prevBtn) prevBtn.addEventListener('click', function () { seekStep(-1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { seekStep(1); });
        if (scrub) {
            scrub.addEventListener('input', function () { stop(); apply(+scrub.value / 1000, false); });
        }
        if (speedSel) {
            var sp = String(settingLoad('speed', '1'));
            if (speedSel.querySelector('option[value="' + sp + '"]')) speedSel.value = sp;
            state.speed = +speedSel.value || 1;
            speedSel.addEventListener('change', function () { state.speed = +speedSel.value || 1; settingSave('speed', speedSel.value); });
        }
        var bind = function (id, fn) { var b = $(id); if (b) b.addEventListener('click', fn); };
        bind('fk-rot-l', function () { orbit.stopIntro(); orbit.rotate(0.35, 0); });
        bind('fk-rot-r', function () { orbit.stopIntro(); orbit.rotate(-0.35, 0); });
        bind('fk-zoom-in', function () { orbit.zoom(1 / 1.2); });
        bind('fk-zoom-out', function () { orbit.zoom(1.2); });
        bind('fk-reset', function () { if (current) orbit.reset(current); });
        var fsBtn = $('fk-full');
        if (fsBtn && typeof window.__wholeScreen === 'function') {
            var ws = window.__wholeScreen(root.querySelector('.fk-view-panel'), {
                key: SLUG,
                onChange: function (on) { fsBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); setTimeout(resize, 60); },
            });
            fsBtn.addEventListener('click', function () { ws.toggle(); });
        } else if (fsBtn) { fsBtn.hidden = true; }

        // Space plays and pauses — only when nothing else owns the key (a focused button, field or
        // the page's own scrolling outside the viewer).
        document.addEventListener('keydown', function (e) {
            if (e.key !== ' ' || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
            if (typeof window.__keyOwnedByFocus === 'function' && window.__keyOwnedByFocus(e)) return;
            var a = document.activeElement;
            if (a && a !== document.body && !view.contains(a)) return;
            e.preventDefault();
            toggle();
        });

        var lineSaved = settingLoad('line', null), toSaved = settingLoad('to', null);
        [['data-pick-line', lineSaved], ['data-pick-to', toSaved]].forEach(function (pair) {
            if (!pair[1]) return;
            var el = root.querySelector('[' + pair[0] + '="' + pair[1] + '"]');
            if (!el) return;
            var sibs = root.querySelectorAll('[' + pair[0] + ']');
            for (var i = 0; i < sibs.length; i++) { var on = sibs[i] === el; sibs[i].classList.toggle('on', on); sibs[i].setAttribute('aria-pressed', on ? 'true' : 'false'); }
        });

        if (typeof ResizeObserver === 'function') new ResizeObserver(function () { resize(); }).observe(view);
        else window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); });
    }

    // ── boot ────────────────────────────────────────────────────────────────
    function boot(json) {
        data = json;
        for (var i = 0; i < data.knots.length; i++) knotById[data.knots[i].id] = data.knots[i];
        var glOk = initGL();
        if (glOk) orbit.attach();
        wire();
        if (statusEl && glOk) statusEl.hidden = true;
        var want = null;
        try { want = new URL(location.href).searchParams.get('knot'); } catch (_) {}
        var first = (want && knotById[want]) ? want : (CFG.initial && knotById[CFG.initial] ? CFG.initial : data.knots[0].id);
        resize();
        loadKnot(first, false);
        renderPick();
        var early = window.__earlyAction && window.__earlyAction.claim(SLUG, function (act) {
            if (act && act.indexOf('knot:') === 0 && knotById[act.slice(5)]) { loadKnot(act.slice(5), false); renderPick(); play(); }
            else if (act === 'play') play();
        });
        if (!early && glOk) {
            // the finished knot first: the person sees what they are about to tie
            apply(1, false);
        }
        root.classList.add('fk-ready');
        window.__fkDebug = {
            seek: function (t) { stop(); apply(t, false); if (renderer) renderer.render(scene, camera); return state.t; },
            knot: function (id) {
                return new Promise(function (res) {
                    if (!knotById[id]) { res(false); return; }
                    loadKnot(id, false, function () { if (renderer) renderer.render(scene, camera); res(true); });
                });
            },
            view: function (a, e) { orbit.rotate(a, e); if (renderer) renderer.render(scene, camera); },
            ids: function () { return data.knots.map(function (k) { return k.id; }); },
            tighten: function (id) { return (knotById[id] && knotById[id].tighten) || [0.8, 1]; },
            quality: Q,
        };
    }

    if (statusEl) { statusEl.textContent = txt('ui.loading'); statusEl.hidden = false; }
    fetch(CFG.data, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(boot)
        .catch(function (err) {
            fail('load_failed');
            try { if (typeof window.__handleError === 'function') window.__handleError(err, 'fishing_knots_data', { say: txt('ui.load_failed') }); } catch (_) {}
        });
})();
